/**
 *
 *
 * @param name
 * @param type
 * @param content
 * @param roles
 * @returns {Promise<void>}
 */
import {apiDELETE, apiGET, apiPOST} from "./RequestsCore";


/**
 * Creates a template with the provided name, type, content, and it can be used only by the provided roles (roles ids).
 *
 * @param name The display name of that template.
 * @param type The type of that template (by now, and as default, 'test')
 * @param content HTML content that would be used to generate a PDF. Shortcodes allowed.
 * @param roles Array of roles id that can use that template.
 * @returns {Promise<void>}
 */
export async function createTemplate(name, type, content, roles) {
    let data = {
        name: name,
        type: type,
        content: content,
        roles: roles
    };

    return await apiPOST('/api/template', data);
}

/**
 * Updates the content, header and footer of the provided template.
 *
 * @param template_id
 * @param content
 * @param header
 * @param footer
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function updateTemplate(template_id, content, header = null, footer = null) {
    let data = {
        content: content,
    }

    if (!!header) {
        data.header = header;
    }

    if (!!footer) {
        data.footer = footer;
    }

    return await apiPOST('/api/template/' + template_id, data);
}

/**
 * Updates the template authorized roles.
 *
 * @param template_id
 * @param roles
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function updateTemplateRoles(template_id, roles) {
    let data = {
        roles: roles,
    }

    return await apiPOST('/api/template/' + template_id, data);
}

/**
 * Updates the template display name.
 *
 * @param template_id
 * @param display_name
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function updateTemplateDisplayName(template_id, display_name) {
    let data = {
        display_name: display_name,
    }

    return await apiPOST('/api/template/' + template_id + '/display-name', data);
}

/**
 * Gets all the existing templates but only the available for the logged user. It can be returned with or without
 * the content attending to the parameter provided. Avoiding the content will make the request faster.
 *
 * @param without_content
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getAvailableTemplates(without_content = null) {
    let data = {};

    if (without_content !== null) {
        data.without_content = without_content;
    }

    return await apiGET('/api/templates', data);
}

/**
 * Gets the requested template based on provided id.
 *
 * @param id
 * @returns {Promise<null|T>}
 */
export async function getTemplate(id) {
    return await apiGET('/api/template/' + id);
}

/**
 * Gets all existing templates allowed for the logged user.
 *
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getTemplates() {
    return await apiGET('/api/templates');
}

export async function deleteTemplate(template_id) {
    return await apiDELETE('/api/template/' + template_id);
}

/**
 * Activates the provided $role to be allowed to use that $template.
 *
 * @param template_id
 * @param role_id
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function activateTemplateRole(template_id, role_id) {
    let data = {
        role: role_id,
    }

    return await apiPOST('/api/template/' + template_id + '/activate-role', data);
}

/**
 * Deactivates the provided $role to be not allowed to use that $template.
 *
 * @param template_id
 * @param role_id
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function deactivateTemplateRole(template_id, role_id) {
    let data = {
        role: role_id,
    }

    return await apiPOST('/api/template/' + template_id + '/deactivate-role', data);
}
